import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Trophy, MousePointerClick, Globe2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getRankings } from "@/lib/api";
import { REGION_KEYS } from "@/i18n/translations";
import NewBadge from "@/components/NewBadge";
import UpdatedBadge from "@/components/UpdatedBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import AppLogo from "@/components/AppLogo";

const RankingsPage = () => {
  const { t, lang } = useLang();
  const [region, setRegion] = useState("Worldwide");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getRankings(region === "Worldwide" ? "" : region)
      .then((d) => setApps(d.apps || []))
      .finally(() => setLoading(false));
  }, [region]);

  return (
    <main className="hac-container py-12 md:py-16">
      <div className="overline mb-3 flex items-center gap-2">
        <Trophy size={12} /> {t.sections.ranking}
      </div>
      <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-tight mb-3">
        Top {apps.length || 100}
      </h1>
      <p className="text-[#64748B] max-w-2xl mb-10">{t.sections.rankingSub}</p>

      <div className="flex flex-wrap gap-2 mb-10" role="tablist">
        {t.regions.map((r) => {
          const key = REGION_KEYS[r] || r;
          const active = region === key || (region === "Worldwide" && key === "Worldwide");
          return (
            <button
              key={r}
              onClick={() => setRegion(key)}
              data-testid={`rankings-region-${key}`}
              className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                active
                  ? "bg-[#2563EB] text-white border-[#2563EB]"
                  : "bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]"
              }`}
            >
              {r}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#94A3B8]">…</div>
      ) : (
        <>
          <div className="text-xs text-[#94A3B8] mb-3 flex items-center gap-2" data-testid="rankings-methodology">
            <span className="inline-flex items-center gap-1"><MousePointerClick size={11} /> {lang === "nl" ? "Klikken (7d)" : "Clicks (7d)"}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Star size={11} /> {lang === "nl" ? "Gem. rating" : "Avg. rating"}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Globe2 size={11} /> {lang === "nl" ? "Actieve regio's" : "Active regions"}</span>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
            {apps.map((a, i) => (
              <Link
                key={a.slug}
                to={`/apps/${a.slug}`}
                data-testid={`rankings-row-${a.slug}`}
                className="grid grid-cols-12 gap-4 px-4 md:px-6 py-5 border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors items-center group"
              >
                <div className="col-span-2 md:col-span-1 font-mono text-xl md:text-2xl text-[#0F172A]">
                  {String(a.rank).padStart(2, "0")}
                </div>
                <div className="col-span-2 md:col-span-1 shrink-0">
                  <AppLogo app={a} className="w-10 h-10 rounded-lg" textClassName="text-base" />
                </div>
                <div className="col-span-8 md:col-span-4 min-w-0">
                  <div className="font-heading text-base md:text-xl text-[#0F172A] group-hover:text-[#0F172A] transition-colors flex items-center gap-1.5 flex-wrap leading-tight">
                    <span className="truncate">{a.name}</span>
                    <VerifiedBadge app={a} size="sm" />
                    {a.recently_listed && <NewBadge app={a} variant="pill" />}
                    {!a.recently_listed && a.recently_updated && <UpdatedBadge app={a} variant="pill" />}
                  </div>
                  <div className="text-[11px] md:text-xs text-[#94A3B8] truncate">
                    {a.country} · {(a.target_audience || []).slice(0, 3).join(", ")}
                  </div>
                </div>
                <div className="hidden md:grid col-span-4 grid-cols-3 gap-2">
                  <Pillar
                    icon={<MousePointerClick size={11} />}
                    value={a.clicks_7d ?? 0}
                    label={lang === "nl" ? "klikken" : "clicks"}
                    tone={(a.clicks_7d ?? 0) > 0 ? "emerald" : "neutral"}
                    testid={`rankings-clicks-${a.slug}`}
                  />
                  <Pillar
                    icon={<Star size={11} className="fill-[#F59E0B] text-[#F59E0B]" />}
                    value={a.rating?.toFixed(1) ?? "—"}
                    label={lang === "nl" ? "rating" : "rating"}
                    tone="amber"
                    testid={`rankings-rating-${a.slug}`}
                  />
                  <Pillar
                    icon={<Globe2 size={11} />}
                    value={a.regions_count ?? (a.regions || []).length}
                    label={lang === "nl" ? "regio's" : "regions"}
                    tone="sky"
                    testid={`rankings-regions-${a.slug}`}
                  />
                </div>
                <div className="col-span-12 md:hidden">
                  <div className="grid grid-cols-3 gap-2">
                    <Pillar icon={<MousePointerClick size={11} />} value={a.clicks_7d ?? 0} label={lang === "nl" ? "klikken" : "clicks"} tone={(a.clicks_7d ?? 0) > 0 ? "emerald" : "neutral"} testid={`rankings-clicks-mobile-${a.slug}`} />
                    <Pillar icon={<Star size={11} className="fill-[#F59E0B] text-[#F59E0B]" />} value={a.rating?.toFixed(1) ?? "—"} label="rating" tone="amber" testid={`rankings-rating-mobile-${a.slug}`} />
                    <Pillar icon={<Globe2 size={11} />} value={a.regions_count ?? (a.regions || []).length} label={lang === "nl" ? "regio's" : "regions"} tone="sky" testid={`rankings-regions-mobile-${a.slug}`} />
                  </div>
                </div>
                <div className="col-span-6 md:col-span-1 text-right text-xs text-[#64748B] font-mono hidden md:block">
                  {a.pricing?.free_tier && a.pricing?.monthly_usd === 0
                    ? t.common.free
                    : `$${a.pricing?.monthly_usd}/mo`}
                </div>
                <div className="hidden md:flex col-span-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight size={16} className="text-[#0F172A]" />
                </div>
              </Link>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-[#94A3B8] max-w-3xl" data-testid="rankings-footnote">
            {lang === "nl"
              ? "Onze ranking is gebaseerd op een gewogen score uit functionaliteit, gemiddelde rating en regionaal bereik. Click-counts geven de activiteit van deze week weer (reset elke maandag 00:00 UTC) en helpen je trends in één oogopslag zien."
              : "Our ranking is based on a weighted score from feature depth, average rating and regional coverage. Click counts reflect this week's activity (reset every Monday 00:00 UTC) and help you see trends at a glance."}
          </p>
        </>
      )}
    </main>
  );
};

const PILLAR_STYLES = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  neutral: "bg-[#F8FAFC] border-[#E2E8F0] text-[#94A3B8]",
};

const Pillar = ({ icon, value, label, tone = "neutral", testid }) => (
  <div className={`border rounded-lg px-2 py-1.5 ${PILLAR_STYLES[tone]}`} data-testid={testid}>
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-70">
      {icon}
      <span>{label}</span>
    </div>
    <div className="font-mono text-base leading-tight mt-0.5">{value}</div>
  </div>
);

export default RankingsPage;
